<template>
  <vl-region class="region">
    <vl-layout>
      <template v-if="detail && !loading">
        <vl-link-list mod-inline>
          <vl-link-list-item>
            <vl-link :to="backLink" mod-block mod-bold mod-no-visited-state :exact="true">
              {{ isAdmin ? $t('publications.title') : $t('publications.me.title') }}
            </vl-link>
          </vl-link-list-item>
        </vl-link-list>
        <vl-title>{{ detail.productLabel }}</vl-title>
        <vl-grid v-vl-spacer:bottom.small>
          <vl-column>
            <vl-properties>
              <vl-properties-column>
                <vl-properties-list class="properties">
                  <vl-properties-label>{{ $t('publications.detail.publicationType') }}</vl-properties-label>
                  <vl-properties-data>{{
                    detail.publicationType == PublicationTypes.Datasetserie.id
                      ? t('publications.datasetserie')
                      : t('publications.dataset')
                  }}</vl-properties-data>
                  <vl-properties-label>{{ $t('publications.detail.versionDate') }}</vl-properties-label>
                  <vl-properties-data>{{ dateFilter(detail.versionDate) }}</vl-properties-data>
                  <vl-properties-label>{{ $t('publications.detail.publicationDate') }}</vl-properties-label>
                  <vl-properties-data>{{ dateFilter(detail.publicationDate) }}</vl-properties-data>
                  <vl-properties-label>{{ $t('publications.detail.status') }}</vl-properties-label>
                  <vl-properties-data>{{
                    $t(`publications.states.publication.${detail.publicationStatus?.toLowerCase()}`)
                  }}</vl-properties-data>
                  <vl-properties-label>{{ $t('publications.detail.organisation') }}</vl-properties-label>
                  <vl-properties-data>{{ detail.organisationName }}</vl-properties-data>
                </vl-properties-list>
              </vl-properties-column>
            </vl-properties>
          </vl-column>
        </vl-grid>
        <vl-grid v-vl-spacer:bottom.medium class="links-grid">
          <vl-column v-if="metadataUrl">
            <vl-link
              :href="metadataUrl"
              mod-icon-before
              icon="external"
              mod-no-visited-state
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('publications.detail.metadata') }}
            </vl-link>
          </vl-column>
          <vl-column v-if="contractUrl" v-vl-spacer:top.small>
            <vl-link mod-icon-before icon="file-zipped-new" :href="contractUrl" target="_blank">{{
              $t('publications.detail.contract')
            }}</vl-link>
          </vl-column>
          <vl-column v-if="deliverySpecificationsUrl" v-vl-spacer:top.small>
            <vl-link mod-icon-before icon="file-office-pdf" :href="deliverySpecificationsUrl" target="_blank">{{
              $t('publications.detail.deliverySpecifications')
            }}</vl-link>
          </vl-column>
        </vl-grid>
        <vl-grid>
          <vl-column v-if="createButtonDisabledTooltip" width="12">
            <vl-alert icon="warning" :content="createButtonDisabledTooltip" mod-warning />
          </vl-column>
          <vl-column v-vl-spacer:top.medium width="8">
            <vl-title tag-name="h2">{{
              isDatasetserie ? $t('publications.detail.datasets') : $t('publications.detail.deliveries')
            }}</vl-title>
          </vl-column>
          <vl-column v-vl-spacer:top.medium width="4">
            <vl-action-group mod-align-right>
              <vl-button
                v-if="showCreateVersionButton"
                :mod-disabled="disableCreateVersionButton"
                :v-vl-tooltip="createButtonDisabledTooltip"
                :a11y-label="createButtonDisabledTooltip"
                mod-primary
                mod-large
                type="button"
                @click="onCreateVersionClick"
                >{{ isDatasetserie ? t('publications.detail.newDataset') : t('publications.detail.newVersion') }}
              </vl-button>
            </vl-action-group>
          </vl-column>
          <vl-column v-if="automaticDeliveryDescription" width="12">
            <!-- eslint-disable vue/no-v-html -->
            <p v-html="automaticDeliveryDescription"></p>
            <!-- eslint-enable vue/no-v-html --></vl-column
          >
          <vl-column width="12" width-m="12" width-s="12">
            <PublicationVersionsDatatable :key="id" :publication-id="id" />
          </vl-column>
        </vl-grid>
      </template>

      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('publications.detail.loading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePublicationDetailStore } from '../store/publication-detail.store';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/store/user.store';
import { ENUM_PublicationTypes } from '@/modules/core/common';
import { overviewLocation } from '@/modules/publications/common';
import { dateFilter } from '@/filters';
import PublicationVersionsDatatable from './subcomponents/publication-versions-datatable/PublicationVersionsDatatable.vue';
import { config } from '@/common/config/config';
import { usePublicationCreateStore } from '../store/publication-create.store';

const { t } = useI18n();
const detailStore = usePublicationDetailStore();
const createStore = usePublicationCreateStore();
const userStore = useUserStore();

const route = useRoute();
const router = useRouter();

const isAdmin = computed(() => {
  return userStore.isSysteembeheerder;
});

const PublicationTypes = computed(() => {
  return ENUM_PublicationTypes;
});

// Read once, when the page is built: only the overview writes it, and it cannot change from here.
const backLink = overviewLocation({ name: 'publications' });

const id = computed((): string => {
  return route.params.id as string;
});

const detail = computed(() => {
  return detailStore.publication;
});

const isDatasetserie = computed((): boolean => {
  return detail.value?.publicationType === ENUM_PublicationTypes.Datasetserie.id;
});

const isAutomaticDelivery = computed((): boolean => {
  return !!detail.value?.deliveryTriggerSchedule;
});

const showCreateVersionButton = computed((): boolean => {
  return !!newPublicationCheck.value?.hasActiveDefinition && !!newPublicationCheck.value?.allowPublication;
});

const disableCreateVersionButton = computed((): boolean => {
  return !!createButtonDisabledTooltip.value;
});

const automaticDeliveryDescription = computed((): string => {
  if (isAutomaticDelivery.value) {
    return t('publications.detail.automaticDeliveryDescription', {
      schedule: detail.value?.deliveryTriggerSchedule?.toLowerCase(),
      source: getTriggerSource(detail.value?.deliveryTriggerSourceId)
    });
  }
  return '';
});

const loading = computed((): boolean => {
  return detailStore.loading;
});

const newPublicationCheck = computed(() => {
  return detail.value?.newPublicationCheck;
});

const createButtonDisabledTooltip = computed((): string => {
  if (newPublicationCheck.value) {
    if (newPublicationCheck.value.hasPendingDelivery || newPublicationCheck.value.hasPendingPublication) {
      return t('publications.create.pendingPublicationTooltip');
    }
  }
  return '';
});

const onCreateVersionClick = (): void => {
  router.push({ name: 'publication.create', params: { id: detail.value?.productDefinitionId } });
};

const getTriggerSource = (sourceId: number): string => {
  switch (sourceId) {
    case 1:
      return t('publications.detail.triggerSource.database');
    case 2:
      return t('publications.detail.triggerSource.cloudStorage');
    case 3:
      return t('publications.detail.triggerSource.ftpServer');
    case 4:
      return t('publications.detail.triggerSource.webService');
    case 5:
      return t('publications.detail.triggerSource.website');
    default:
      return '';
  }
};

const metadataUrl = computed((): string | null => {
  const uuid = detail.value?.productMetadataUUID;
  return uuid ? `${config.integrations.metadataUrl}/${uuid}`.toString() : null;
});

const contractUrl = computed((): string | null => {
  return ''; // TODO
});

const deliverySpecificationsUrl = computed((): string | null => {
  return ''; // TODO
});

onMounted(async () => {
  await detailStore.getPublication(id.value);
});

onUnmounted(() => {
  detailStore.clear();
  createStore.clear();
});
</script>

<style lang="scss" scoped>
.properties .vl-properties__label,
.properties .vl-properties__data {
  padding-bottom: 5px;
}

.links-grid .vl-link {
  margin-bottom: 10px;
}
</style>
